import type { FC } from "react";

import { ArticleCard } from "@/ui/components/data-display";
import { toOptions } from "@/ui/components/form";
import {
  Column,
  Grid,
  Section,
  Stack,
} from "@/ui/components/layout";
import { Pagination } from "@/ui/components/navigation";
import {
  ARTICLE_FEED_LOADING_CARD_COUNT,
  ARTICLE_FEED_SECTION_ID,
  type TArticleFeedProps,
} from "@/ui/components/sections/articles/ArticleFeed/ArticleFeed.config";
import {
  useArticleFeed,
} from "@/ui/components/sections/articles/ArticleFeed/useArticleFeed";
import {
  ArticleCategoryFilter,
} from "@/ui/components/sections/articles/ArticleCategoryFilter";
import {
  transformArticleToArticleCard,
} from "@/ui/components/data-display/Card/ArticleCard/ArticleCard.transformers";
import {
  ArticleFeedCardsSkeleton,
} from "./skeletons/ArticleFeedCards.skeleton";
import { ArticleFeedHeader } from "./ArticleFeedHeader";

export const ArticleFeed: FC<TArticleFeedProps> = (
  {
    title,
    body,
    categories = [],
    articles = [],
    paginationMeta,
    isArticlesLoading,
    isCategoriesLoading,
  },
) => {
  const { handlePageChange } = useArticleFeed();

  return (
    <Section id={ARTICLE_FEED_SECTION_ID}>
      <Stack gap="6">
        <ArticleFeedHeader title={title} body={body} />
        <Stack gap="6">
          <ArticleCategoryFilter
            options={toOptions(categories, {
              label: "title",
              value: "slug",
            })}
            isLoading={isCategoriesLoading}
          />
          { isArticlesLoading ? (
            <ArticleFeedCardsSkeleton
              cardCount={paginationMeta?.entriesPerPage
              ?? ARTICLE_FEED_LOADING_CARD_COUNT}
            />
          ): (
            <Grid tag="ul" gap="6">
              { articles.map((article) => (
                <Column
                  tag="li"
                  key={article.id}
                  spans={{ xl: "4" }}
                >
                  <ArticleCard {...transformArticleToArticleCard(article)} />
                </Column>
              )) }
            </Grid>
          ) }
          { paginationMeta && (
            <Pagination
              page={paginationMeta.currentPage}
              totalPages={paginationMeta.totalPages}
              onPageChange={handlePageChange}
            />
          ) }
        </Stack>
      </Stack>
    </Section>
  );
};

ArticleFeed.displayName = "ArticleFeed";
